import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Grid, Typography } from '@mui/material';
import { values } from 'lodash';
import WizardArchTypeChip from 'pages/GettingStarted/Wizard/components/WizardArchTypeChip';
import WizardBackButton from 'pages/GettingStarted/Wizard/components/WizardBackLink';
import WizardCard from 'pages/GettingStarted/Wizard/components/WizardCard';
import WizardPrimaryButton from 'pages/GettingStarted/Wizard/components/WizardPrimaryButton';
import WizardContentLayout from 'pages/GettingStarted/Wizard/Phases/WizardContentLayout';
import {
	NONE_AUTH_METHOD,
	PRIMARY_METHODS_BTB,
	PRIMARY_METHODS_BTC,
	WIZARD_PAGES
} from 'pages/GettingStarted/Wizard/utils/consts';
import React, { FC } from 'react';
import { ArchType, IWizardProps, WizardAuthMethod } from 'types/wizard';
import { useWizardUpdate } from '../../utils/hook';

const SecondaryMethod: FC<IWizardProps> = ({ state }) => {
	const { archType, secondaryMethod, primaryMethods } = state;

	const { update } = useWizardUpdate(state);

	const [selected, setSelected] = React.useState(
		secondaryMethod || NONE_AUTH_METHOD
	);

	const handleNext = React.useCallback(() => {
		update({
			secondaryMethod: selected,
			page: WIZARD_PAGES.suggestions
		});
	}, [update, selected]);

	const handleBack = React.useCallback(() => {
		update({
			page: WIZARD_PAGES.primaryMethods
		});
	}, [update]);

	const methods =
		archType === ArchType.B2C ? PRIMARY_METHODS_BTC : PRIMARY_METHODS_BTB;

	const skip = !values(WizardAuthMethod).includes(selected as WizardAuthMethod);

	return (
		<WizardContentLayout
			{...{ state }}
			backButton={<WizardBackButton onClick={handleBack} />}
			actionButton={
				skip ? (
					<Button
						data-testid="wizard-skip-button"
						onClick={handleNext}
						endIcon={<ArrowForwardIcon />}
					>
						<Typography variant="body1">
							{intl.tr('wizard.methods.no.mfa.button')}
						</Typography>
					</Button>
				) : (
					<WizardPrimaryButton onClick={handleNext} disabled={false} />
				)
			}
		>
			<Box display="flex" flexDirection="column">
				<Box
					display="flex"
					flexDirection={{
						xs: 'column',
						sm: 'row'
					}}
				>
					<Box
						display="flex"
						flexDirection={{
							xs: 'row',
							sm: 'column'
						}}
						pl={{
							xs: 0,
							sm: 2
						}}
					>
						<WizardArchTypeChip archType={archType} />
					</Box>
					<Box
						display="flex"
						flexDirection="column"
						flexGrow={1}
						pl={{
							xs: 0,
							sm: 2
						}}
					>
						<Typography variant="h6">
							{intl.tr('wizard.phase.build.secondaryMethod.label')}
						</Typography>
						<Typography variant="subtitle1">
							{intl.tr('wizard.phase.build.secondaryMethod.label.secondary')}
						</Typography>
					</Box>
				</Box>
				<Grid
					container
					display="flex"
					flexGrow={5}
					justifyContent="flex-start"
					pt={4}
					pb={1}
					pl={1}
				>
					<Grid item xs={12}>
						<Box display="flex" flexDirection="row" flexGrow={1}>
							<Grid container spacing={4}>
								{methods.map((method) => {
									let cardTag = '';
									const primarySelected = primaryMethods.includes(method.value);
									const isSelected = selected === method.value;
									if (primarySelected) {
										cardTag = intl.tr('wizard.phase.build.card.tag.primary');
									} else if (isSelected) {
										cardTag = intl.tr('wizard.phase.build.card.tag.secondary');
									}
									// make sure enchanted link and magic link are not selected together
									const linkDisabled =
										(method.value === WizardAuthMethod.MagicLink &&
											primaryMethods.includes(
												WizardAuthMethod.EnchantedLink
											)) ||
										(method.value === WizardAuthMethod.EnchantedLink &&
											primaryMethods.includes(WizardAuthMethod.MagicLink));
									const disabled =
										primarySelected ||
										method.disabledOnSecondary ||
										linkDisabled;
									return (
										<Grid
											item
											key={method.value}
											data-testid={`wizard-secondary-method-card-${method.value}`}
										>
											<WizardCard
												size="small"
												label={intl.tr(method.label)}
												Icon={method.icon}
												selected={primarySelected}
												secondarySelected={isSelected}
												onClick={() =>
													setSelected(
														selected === method.value
															? NONE_AUTH_METHOD
															: method.value
													)
												}
												disabled={disabled}
												cardTag={cardTag}
												subtitle={
													method.subtitle
														? intl.tr(`wizard.methods.${method.value}`)
														: ''
												}
											/>
										</Grid>
									);
								})}
							</Grid>
						</Box>
					</Grid>
				</Grid>
			</Box>
		</WizardContentLayout>
	);
};

export default SecondaryMethod;
